import { useEffect, useState } from 'react';
import { DateValue } from 'react-aria-components';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { getRentValues } from '@flxlease/flexin-utils';
import { getLocalTimeZone, parseDate, today } from '@internationalized/date';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { RangeValue } from '@react-types/shared';
import isEmpty from 'lodash/isEmpty';
import { useQuery } from 'urql';

import { GET_CALENDAR_EVENTS_BY_PROPERTY } from '../../../../Graphql/queries';
import { formatUsd } from '../../../../Utils/GenericFunctions';
import { PHONE_REGEX } from '../../../../Utils/constants';
import { DateRangePicker } from '../../../components/dateRangePicker/DateRangePicker';
import { BookingFormInputs, IBookingFormProps } from '../types';
import { BookingFooter } from './BookingFooter';

const StickyReserve = styled(Box)`
  display: flex;
  justify-content: space-between;
  mt: auto;
  position: sticky;
  bottom: 0;
  background-color: #fff;
  padding: 2;
  border-top: 1px solid rgb(221, 221, 221);
  padding: 15px;
`;

export const FieldMargin = styled('div')`
  margin-bottom: 15px;
`;

const Form = styled('form')`
  padding: 25px;
`;

export const BookingFormMobile = ({
  listing,
  onSubmit,
  isSubmitting,
  isSubmitted,
  canCancelReservation = false,
  onCancelClick,
}: IBookingFormProps) => {
  const { monthRent, securityDeposit, id: propertyId } = listing;
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<BookingFormInputs>();

  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState({} as RangeValue<DateValue>);

  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => {
    setDrawerOpen(false);
  }, [isSubmitted]);

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }

    setDrawerOpen(open);
  };

  const { termLengthDays, totalRent, baseRent } = getRentValues(
    listing.baseRent,
    monthRent,
    dateRange,
  );
  const tomorrow = today(getLocalTimeZone()).add({ days: 1 });
  const grandTotal = totalRent + (securityDeposit || 0);

  const [calendarEventsResult] = useQuery({
    query: GET_CALENDAR_EVENTS_BY_PROPERTY,
    variables: { propertyId: propertyId },
    pause: !propertyId,
  });

  const min30Days = (range: RangeValue<DateValue>) =>
    !range?.start || range?.end.compare(range.start) + 1 < 30
      ? 'Minimum stay length is 30 days.'
      : null;
  const bookedRanges = calendarEventsResult?.data?.getCalendarEventsByProperty.map((event) => [
    parseDate(event.start.split('T')[0]),
    parseDate(event.end.split('T')[0]),
  ]);
  const checkIfDateIsBooked = (date: DateValue) =>
    bookedRanges?.some(
      (interval) => date.compare(interval[0]) >= 0 && date.compare(interval[1]) <= 0,
    ) || false;

  return (
    <>
      <StickyReserve>
        <Button variant='contained' color='primary' onClick={toggleDrawer(true)}>
          Reserve
        </Button>
        <Typography
          variant='h6'
          component='h6'
          fontWeight='bold'
          sx={{ marginBottom: '15px' }}
        >
          {monthRent?.length
            ? `${formatUsd(Math.min(...monthRent))} - ${formatUsd(Math.max(...monthRent))}`
            : formatUsd(baseRent)}
          / month
        </Typography>
      </StickyReserve>
      <Drawer
        anchor='bottom'
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          },
        }}
      >
        <Form onSubmit={onSubmit && handleSubmit(onSubmit)}>
          <FieldMargin>
            <Controller
              control={control}
              name='stayDates'
              render={({ field }) => (
                <DateRangePicker
                  value={dateRange} // Needed to prevent drawer close from clearing rangepicker state
                  monthRent={monthRent}
                  onChange={(dateRange) => {
                    setDateRange(dateRange);
                    field.onChange(dateRange);
                  }}
                  label='Stay dates'
                  minValue={tomorrow}
                  validate={min30Days}
                  isDateUnavailable={checkIfDateIsBooked}
                  isRequired
                />
              )}
            />
          </FieldMargin>
          <FieldMargin>
            <TextField
              fullWidth
              helperText='(123) 456-7890'
              error={errors.phoneNumber ? true : false}
              label='Phone Number'
              type='phone'
              {...register('phoneNumber', {
                required: true,
                pattern: PHONE_REGEX,
              })}
            />
          </FieldMargin>
          <TextField
            fullWidth
            error={errors.message ? true : false}
            label='Message'
            multiline
            rows={4}
            {...register('message', {
              required: true,
            })}
          />
          {!isEmpty(sessionStorage.getItem('userId')) ? (
            <Button
              type='submit'
              sx={{ marginTop: '10px' }}
              color='primary'
              variant='contained'
              disabled={isSubmitting || isSubmitted}
              fullWidth
            >
              Book
            </Button>
          ) : (
            <Button
              sx={{ marginTop: '10px' }}
              color='primary'
              variant='contained'
              disabled={true}
              fullWidth
            >
              Please log in to book
            </Button>
          )}
          <BookingFooter
            baseRent={baseRent}
            grandTotal={grandTotal}
            securityDeposit={securityDeposit || 0}
            totalRent={totalRent}
            termLength={termLengthDays}
            canCancelReservation={canCancelReservation}
            onCancelClick={onCancelClick}
          />
        </Form>
      </Drawer>
    </>
  );
};
